import { useState, useEffect } from 'react';
import { Spin, Typography } from 'antd';
import { LiveKitRoom, VideoConference, useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';
import { useApi } from '../../hooks/use_api';
import './VideoCall.css';

const { Text } = Typography;

function DisconnectOnUnload() {
  const room = useRoomContext();
  useEffect(() => {
    const handleBeforeUnload = () => room.disconnect();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [room]);
  return null;
}

const VideoCallRoom = ({ roomName, participantIdentity, entityId, onLeave, fullscreen = false, userChoices = null }) => {
  const api = useApi();
  const [livekitToken, setLivekitToken] = useState(null);
  const [livekitUrl, setLivekitUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!roomName || !participantIdentity) return;

    const fetchToken = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.post('/api/livekit/token', {
          room_name: roomName,
          // A display-name hint only: the server always mints the LiveKit
          // identity as the caller's user id (so call minutes can be
          // attributed to them) and uses this, or the profile name, as the
          // name other participants see.
          participant_identity: participantIdentity,
          // For a remote (shadow) entity the server routes token minting to the
          // entity's home server; for a local entity this is ignored.
          entity_id: entityId != null ? Number(entityId) : null,
        });
        setLivekitToken(data.token);
        setLivekitUrl(data.url);
      } catch (err) {
        setError(err.message || 'An error occurred while connecting to the video call.');
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [roomName, participantIdentity, entityId]);

  if (loading) {
    return (
      <div className="video-call-loading">
        <Spin size="large" />
        <Text type="secondary" style={{ marginTop: 12 }}>
          Connecting to video call...
        </Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-call-error">
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  return (
    <div className={`video-call-room${fullscreen ? ' fullscreen' : ''}`}>
      <LiveKitRoom
        video={userChoices ? userChoices.videoEnabled : true}
        audio={userChoices ? userChoices.audioEnabled : true}
        token={livekitToken}
        serverUrl={livekitUrl}
        onDisconnected={onLeave}
        data-lk-theme="default"
        options={{
          // adaptiveStream: subscribers only receive the resolution each tile is
          // rendered at, and nothing for tiles off-screen or on another grid page.
          // dynacast: publishers stop encoding simulcast layers nobody is watching.
          // Both default to off in livekit-client; without them every participant
          // downloads every camera at full quality, which is most of the LiveKit bill.
          adaptiveStream: true,
          dynacast: true,
          audioCaptureDefaults: userChoices?.audioDeviceId ? { deviceId: userChoices.audioDeviceId } : undefined,
          videoCaptureDefaults: userChoices?.videoDeviceId ? { deviceId: userChoices.videoDeviceId } : undefined,
        }}
      >
        <DisconnectOnUnload />
        <VideoConference />
      </LiveKitRoom>
    </div>
  );
};

export default VideoCallRoom;
